import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n.tsx';

const VIEW = 260;
const OUTPUT = 512;

/**
 * Choose which part of a picture becomes the circle.
 *
 * Drag to position, slide to zoom; the dimmed ring shows exactly what everyone
 * else will see. The result is rendered to a 512px square before upload, which
 * kills two birds: the sender decides the framing rather than every viewer's
 * `object-fit`, and a 12MB camera photo does not travel to every contact when
 * a few hundred kilobytes carry the same face.
 */
export function AvatarCropDialog({
  file,
  onCancel,
  onDone,
}: {
  file: File;
  onCancel: () => void;
  onDone: (bytes: Uint8Array, meta: { mime: string; name: string }) => void;
}) {
  const t = useT();
  const [url, setUrl] = useState<string>();
  const imageRef = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number }>();
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<
    { id: number; startX: number; startY: number; origX: number; origY: number } | undefined
  >(undefined);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  /** Scale that makes the image just cover the viewport, before zoom. */
  const coverScale = natural ? VIEW / Math.min(natural.w, natural.h) : 1;
  const scale = coverScale * zoom;

  /** The image must always cover the circle: clamp the pan to the slack. */
  function clamp(p: { x: number; y: number }, z = zoom): { x: number; y: number } {
    if (!natural) return p;
    const s = coverScale * z;
    const maxX = Math.max(0, (natural.w * s - VIEW) / 2);
    const maxY = Math.max(0, (natural.h * s - VIEW) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  }

  async function confirm() {
    const image = imageRef.current;
    if (!image || !natural) return;
    setBusy(true);
    try {
      // The viewport maps back onto the source: its centre sits at the image
      // centre displaced by the pan, and its edge length is VIEW in screen
      // pixels, so VIEW/scale in source pixels.
      const sSize = VIEW / scale;
      const cx = natural.w / 2 - pos.x / scale;
      const cy = natural.h / 2 - pos.y / scale;
      const sx = Math.max(0, Math.min(natural.w - sSize, cx - sSize / 2));
      const sy = Math.max(0, Math.min(natural.h - sSize, cy - sSize / 2));

      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      canvas.getContext('2d')!.drawImage(image, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.88));
      if (!blob) throw new Error('encode failed');
      onDone(new Uint8Array(await blob.arrayBuffer()), { mime: 'image/jpeg', name: 'avatar.jpg' });
    } catch {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" onClick={onCancel}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('avatar.crop.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('avatar.crop.title')}</h2>
          <button className="icon" onClick={onCancel} aria-label={t('profile.name.cancel')}>
            ✕
          </button>
        </header>
        <p className="hint">{t('avatar.crop.hint')}</p>

        <div
          className="crop__viewport"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            dragRef.current = { id: e.pointerId, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
          }}
          onPointerMove={(e) => {
            const drag = dragRef.current;
            if (!drag || drag.id !== e.pointerId) return;
            setPos(clamp({ x: drag.origX + (e.clientX - drag.startX), y: drag.origY + (e.clientY - drag.startY) }));
          }}
          onPointerUp={() => (dragRef.current = undefined)}
          onPointerCancel={() => (dragRef.current = undefined)}
        >
          {url && !loadFailed && (
            <img
              ref={imageRef}
              src={url}
              alt=""
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setLoadFailed(true)}
              style={{
                width: natural ? natural.w * scale : undefined,
                height: natural ? natural.h * scale : undefined,
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
              }}
            />
          )}
          {loadFailed && (
            /* A decode failure used to be a silent black square. Say what
               happened, and show the file's own story -- the mime type is how
               we found out a phone was handing us HEIC. */
            <div className="crop__error">
              <span>{t('avatar.crop.loadFailed')}</span>
              <code dir="ltr">
                {(file.type || 'unknown type') + ' · ' + Math.max(1, Math.round(file.size / 1024)) + ' KB'}
              </code>
            </div>
          )}
          {!loadFailed && <div className="crop__mask" aria-hidden="true" />}
        </div>

        <input
          className="crop__zoom"
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(e) => {
            const z = Number(e.target.value);
            setZoom(z);
            setPos((p) => clamp(p, z));
          }}
          aria-label={t('avatar.crop.zoom')}
        />

        <button className="primary" disabled={busy || !natural} onClick={() => void confirm()}>
          {t('profile.name.save')}
        </button>
      </div>
    </div>
  );
}
